/* eslint-disable react/no-array-index-key */
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Title } from 'ui-library';
import { getClassNames } from 'ui-library/utils';
import { InfoErrorList } from 'components/InfoErrorList/InfoErrorList';
import ButtonsBlockRow from 'components/ButtonsBlockRow';
import RouteBasedStepper from 'components/Stepper/RouteBasedStepper/RouteBasedStepper';

import './RouteBasedStepperLayout.css';

interface PortfolioFlowLayoutProps {
    dataSource: () => any;
    children: React.ReactNode;
}

const RouteBasedStepperLayout = ({
    dataSource,
    children,
}: PortfolioFlowLayoutProps) => {
    const {
        stepper,
        footerAdditionalInfo,
        onBack,
        onContinue,
        continueLabel,
        continueDisabled,
        backLabel,
        backDisabled,
        pageErrors,
        onAdditionalComponentClick,
        additionalComponentLabel,
        inProgress,
        isStepperEnabled,
        isFooterSimplified,
        contentTitle,
        contentClassName,
        className,
        hideStepperButtons,
        productType,
        isLoading,
        submitting,
        allowJumpStep = false,
        showPercentage = false,
    } = dataSource();

    const {
        t,
        i18n: { language },
    } = useTranslation();
    const contentClassnames = useMemo(() => (
        getClassNames(
            contentClassName,
        )
    ), [contentClassName]);

    const ButtonsBlock = useMemo(() => (
        <ButtonsBlockRow
            additionalButton={{
                text: backLabel || t('portfolio.back'),
                onClick: onBack,
                disabled: backDisabled,
            }}
            additionalComponent={onAdditionalComponentClick
                && (
                    <Button
                        type="secondary"
                        disabled={continueDisabled}
                        onClick={onAdditionalComponentClick}
                    >
                        {additionalComponentLabel}
                    </Button>
                )}
            primaryButton={{
                text: continueLabel || t('portfolio.continue'),
                onClick: onContinue,
                disabled: continueDisabled,
                loading: inProgress || isLoading || submitting,
            }}
        />
    ), [
        language,
        backLabel,
        backDisabled,
        onAdditionalComponentClick,
        continueDisabled,
        additionalComponentLabel,
        continueLabel,
        onBack,
        onContinue,
        continueDisabled,
        inProgress,
    ]);

    return (
        <div className={`${className} RouteBasedStepperLayout`}>
            <div className="fi-body">
                {isStepperEnabled && (
                    <RouteBasedStepper
                        config={stepper.steps}
                        currentStepNumber={stepper.currentStepNumber}
                        title={stepper.title}
                        productType={productType}
                        allowJumpStep={allowJumpStep}
                        showPercentage={showPercentage}
                    />
                )}
                <div className="container">
                    {stepper?.steps?.length > 0 ? (
                        <>
                            <div className={contentClassnames}>
                                {contentTitle && (
                                    <Title type={1} className="font-normal">
                                        {t(contentTitle)}
                                    </Title>
                                )}

                                {children}
                            </div>
                            <InfoErrorList errors={pageErrors}/>
                        </>
                    ) : ''}

                </div>
            </div>

            {isFooterSimplified ? (
                <div className="fi-footer">
                    <div className="container">
                        <div className="fi-footer-data">
                            <div className="fi-footer-client-details">
                                {
                                    footerAdditionalInfo.map(({
                                        label,
                                        values,
                                    }, i) => (
                                        <div
                                            key={`item${i}`}
                                            className="fi-footer-client-details__block fi-footer-client-details__block--age"
                                        >
                                            <div className="fi-footer-client-details_label">{t(label)}</div>
                                            {
                                                values.map((value, j) => (
                                                    <div
                                                        key={`value${j}`}
                                                        className="fi-footer-client-details__value"
                                                    >
                                                        {value}
                                                    </div>
                                                ))
                                            }
                                        </div>
                                    ))
                                }
                            </div>
                            {!hideStepperButtons && ButtonsBlock}
                        </div>
                    </div>
                </div>
            ) : (
                <div className="container">
                    {!hideStepperButtons && ButtonsBlock}
                </div>
            )}
        </div>
    );
};

export default RouteBasedStepperLayout;
